import React, { useState, useEffect, useCallback } from 'react';
import { readBlobError, triggerBlobDownload } from '../utils/exportHelpers';
import { PageHeader, StatCards, Card, TabBar, Table, Field, inputStyle,
         btnPrimary, btnSm, Empty, fmtKES , useBreakpoint } from '../components/UI';
import * as api from '../api/client';
import axiosClient from '../api/client';
import { getBrandingSlug } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import { printIssuanceVoucher, printShoppingList } from '../utils/print';

// ─── Approval Stamp SVG ───────────────────────────────────────────────────────
const STAMP_CFG = {
  storekeeper: { label:'STOREKEEPER', scaleFont:0.85 },
  manager:     { label:'MANAGER',     scaleFont:1    },
  accountant:  { label:'ACCOUNTANT',  scaleFont:0.82 },
  director:    { label:'DIRECTOR',    scaleFont:0.9  },
  hr:          { label:'HR',          scaleFont:1.1  },
};
function ApprovalStamp({ role, by, at, size=72 }) {
  const cfg = STAMP_CFG[role] || { label:role.toUpperCase(), scaleFont:1 };
  const cx = size/2, cy = size/2;
  const rOuter = cx - 3, rInner = cx - 10;
  const arcR = rOuter - 5;
  const uid = `sk_stamp_${role}_${size}`;
  const mainFontSize = Math.round(9 * cfg.scaleFont * (size/72));
  return (
    <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:3 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ opacity:0.9 }}>
        <defs>
          <path id={`${uid}_top`} d={`M ${cx-arcR},${cy} a ${arcR},${arcR} 0 1,1 ${arcR*2},0`}/>
          <filter id={`${uid}_d`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="3" result="n"/>
            <feDisplacementMap in="SourceGraphic" in2="n" scale="0.9" xChannelSelector="R" yChannelSelector="G"/>
          </filter>
        </defs>
        <circle cx={cx} cy={cy} r={rOuter} fill="#fdf6ec" stroke="#8B1A1A" strokeWidth="2.2" filter={`url(#${uid}_d)`}/>
        <circle cx={cx} cy={cy} r={rInner} fill="none" stroke="#8B1A1A" strokeWidth="1.2" filter={`url(#${uid}_d)`}/>
        <text fontSize={Math.max(5,size*0.077)} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="800" letterSpacing="1.4">
          <textPath href={`#${uid}_top`} startOffset="15%">GRAND MEMORIES</textPath>
        </text>
        <line x1={cx-rInner+5} y1={cy-rInner*0.40} x2={cx+rInner-5} y2={cy-rInner*0.40} stroke="#8B1A1A" strokeWidth="0.9"/>
        <line x1={cx-rInner+5} y1={cy+rInner*0.20} x2={cx+rInner-5} y2={cy+rInner*0.20} stroke="#8B1A1A" strokeWidth="0.9"/>
        <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle" fontSize={mainFontSize}
          fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="900" letterSpacing="0.3"
          filter={`url(#${uid}_d)`}>{cfg.label}</text>
        <text x={cx} y={cy+rInner*0.38} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4.5,size*0.069)} fill="#8B1A1A"
          fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="600" letterSpacing="1.8">— APPROVED —</text>
        <text x={cx-rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx+rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx} y={cy+rInner*0.85} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4,size*0.069)} fill="#8B1A1A"
          fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="700" letterSpacing="1.6">RESORT &amp; SPA</text>
      </svg>
      {(by||at) && (
        <div style={{ fontSize:9, color:'#888', textAlign:'center', maxWidth:size, lineHeight:1.3 }}>
          {by && <div style={{ fontWeight:600 }}>{by}</div>}
          {at  && <div>{new Date(at).toLocaleDateString('en-KE',{day:'2-digit',month:'short',year:'numeric'})}</div>}
        </div>
      )}
    </div>
  );
}

const DEPARTMENTS = [
  'kitchen','bar','housekeeping','restaurant','reception',
  'conference','maintenance','pool','spa','gift_shop','admin','other',
];
const DEPT_LABELS = {
  kitchen:'Kitchen', bar:'Bar', housekeeping:'Housekeeping', restaurant:'Restaurant',
  reception:'Reception', conference:'Conference', maintenance:'Maintenance',
  pool:'Pool & Recreation', spa:'Spa & Wellness', gift_shop:'Gift Shop',
  admin:'Administration', other:'Other',
};
const CATEGORIES = [
  {v:'food',label:'Food & Perishables'},{v:'beverage',label:'Beverages'},
  {v:'cleaning',label:'Cleaning & Hygiene'},{v:'linen',label:'Linen & Bedding'},
  {v:'stationery',label:'Stationery & Office'},{v:'maintenance',label:'Maintenance & Tools'},
  {v:'amenities',label:'Guest Amenities'},{v:'chemicals',label:'Chemicals'},
  {v:'uniforms',label:'Uniforms'},{v:'other',label:'Other'},
];
const ADJ_TYPES = [
  {v:'receive',label:'📦 Receive / Purchase'},
  {v:'count',label:'🔢 Stock Count Correction'},
  {v:'writeoff',label:'🗑️ Write-Off / Damage'},
  {v:'return',label:'↩️ Return to Supplier'},
  {v:'other',label:'📝 Other'},
];
const UNITS = ['kg','g','l','ml','piece','crate','bunch','tin','packet','dozen','box','roll','bottle','bag','carton'];

const CAT_COLORS = {
  food:'#e67e22',beverage:'#3498db',cleaning:'#2ecc71',linen:'#9b59b6',
  stationery:'#1abc9c',maintenance:'#e74c3c',amenities:'#f39c12',
  chemicals:'#e91e8c',uniforms:'#00b300',other:'#888',
};

export default function StorekeeperPage({ notify }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('stock');
  const [items, setItems]           = useState([]);
  const [issuances, setIssuances]   = useState([]);
  const [adjLogs, setAdjLogs]       = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [shoppingLists, setShoppingLists] = useState([]);
  const [purchases, setPurchases]         = useState([]);

  const load = useCallback(async () => {
    try {
      const [it, is, adj, su, sl, pu] = await Promise.all([
        api.getStockItems(),
        api.getIssuances(),
        api.getStockAdjustments(),
        api.getUsers(),
        api.getShoppingLists(),
        api.getPurchases(),
      ]);
      setItems(it.data.results || it.data);
      setIssuances(is.data.results || is.data);
      setAdjLogs(adj.data.results || adj.data);
      setStaffUsers(su.data.results || su.data);
      setShoppingLists(sl.data.results || sl.data);
      setPurchases(pu.data.results || pu.data);
    } catch(e) { notify('Failed to load store data', 'error'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const lowStockItems    = items.filter(i => i.is_low_stock);
  const totalStockValue  = items.reduce((s,i) => s + (parseFloat(i.stock_value)||0), 0);
  const pendingIssuances = issuances.filter(i => i.status === 'draft').length;
  const todayIssuances   = issuances.filter(i => i.issued_at?.startsWith(new Date().toISOString().slice(0,10))).length;
  const pendingSL        = shoppingLists.filter(s => s.status === 'pending').length;
  const draftPurchases   = purchases.filter(p => p.status === 'draft').length;

  const doExportMovement = async (type = 'excel') => {
    notify(`Preparing ${type.toUpperCase()} export…`);
    try {
      const url = type === 'excel'
        ? api.exportMovementLogExcel()
        : api.exportMovementLogPdf();
      const res = await axiosClient.get(url, { responseType: 'blob', baseURL: '' });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      if (blob.size < 50) throw new Error('Empty file — check server logs');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${getBrandingSlug()}_MovementLog_${new Date().toISOString().slice(0,10)}.${type === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 1000);
      notify(`Movement log exported ✓`);
    } catch(e) { notify(`Export failed: ${await readBlobError(e)}`, 'error'); }
  };

  return (
    <div>
      <PageHeader icon="🏪" title={`Storekeeper — ${user?.name}`}
        sub="Manage stock, take inventory counts, issue items, record purchases & raise purchase requests" />
      <StatCards stats={[
        { label:'Total Stock Items', value:items.length,           color:'#3498db', icon:'📦', sub:`${new Set(items.map(i=>i.category)).size} categories` },
        { label:'Low Stock Alerts',  value:lowStockItems.length,   color:lowStockItems.length > 0 ? '#e74c3c' : '#2ecc71', icon:'⚠️', sub: lowStockItems.length > 0 ? 'Action needed' : 'Stock healthy' },
        { label:'Stock Value',       value:fmtKES(totalStockValue),color:'#c8a96e', icon:'💰' },
        { label:"Today's Issuances", value:todayIssuances,         color:'#9b59b6', icon:'📋' },
      ]} />
      <TabBar tabs={[
        { id:'stock',       label:'Stock Catalogue',    icon:'📦' },
        { id:'adjust',      label:'Stock Adjustment',   icon:'⚖️' },
        { id:'issuance',    label:`Issuances${pendingIssuances>0?` (${pendingIssuances} draft)`:''}`, icon:'📋' },
        { id:'purchases_tab', label:`Purchases${draftPurchases>0?` (${draftPurchases})`:''}`, icon:'🧾' },
        { id:'purchase',    label:`Shopping Lists${pendingSL>0?` (${pendingSL})`:''}`, icon:'🛒' },
        { id:'low',         label:`Low Stock${lowStockItems.length>0?` (${lowStockItems.length})`:''}`, icon:'⚠️' },
        { id:'history',     label:'Movement Log', icon:'📜' },
      ]} active={tab} onChange={setTab} />

      {tab === 'stock'        && <StockCatalogue items={items} notify={notify} onUpdate={load} />}
      {tab === 'adjust'       && <StockAdjustPanel items={items} notify={notify} onUpdate={load} />}
      {tab === 'issuance'     && <IssuancePanel items={items} issuances={issuances} staffUsers={staffUsers} notify={notify} onUpdate={load} />}
      {tab === 'purchases_tab'&& <PurchasesPanel purchases={purchases} items={items} shoppingLists={shoppingLists} notify={notify} onUpdate={load} user={user} />}
      {tab === 'purchase'     && <ShoppingListPanel shoppingLists={shoppingLists} items={lowStockItems} notify={notify} onUpdate={load} user={user} />}
      {tab === 'low'          && <LowStockPanel items={lowStockItems} notify={notify} onUpdate={load} />}
      {tab === 'history'      && <MovementLog logs={adjLogs} onExport={doExportMovement} />}
    </div>
  );
}


/* ─── Shared modal shell ──────────────────────────────────────────────── */
function Modal({ onClose, children, width = 600 }) {
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        zIndex: 999, display: 'flex', alignItems: 'center',
        justifyContent: 'center', overflow: 'auto', padding: 20,
      }}
    >
      <div style={{
        background: 'var(--surface)', borderRadius: 16, width, maxWidth: '95vw',
        boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
        border: '1px solid rgba(201,168,76,0.15)',
        animation: 'slideUp 0.18s ease',
      }}>
        {children}
      </div>
      <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
function ModalHeader({ icon, title, subtitle, onClose }) {
  return (
    <div style={{
      padding: '22px 24px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 22 }}>{icon}</span>
          <h3 style={{ margin: 0, color: '#c8a96e', fontSize: 18, fontWeight: 700 }}>{title}</h3>
        </div>
        {subtitle && <div style={{ color: 'var(--textMuted)', fontSize: 13, paddingLeft: 32 }}>{subtitle}</div>}
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--textMuted)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4 }}>✕</button>
    </div>
  );
}
function ModalFooter({ onSave, onCancel, saveLabel = 'Save', saving = false, errorCount = 0 }) {
  return (
    <div style={{
      padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.06)',
      display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
    }}>
      {errorCount > 0 && (
        <div style={{
          marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 12, color: '#ef4444', fontWeight: 600,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 6, padding: '4px 10px',
        }}>
          <span>⚠</span>
          <span>{errorCount} field{errorCount > 1 ? 's need' : ' needs'} attention</span>
        </div>
      )}
      <button onClick={onCancel} style={{ ...btnSm, minWidth: 90 }}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={{ ...btnPrimary, minWidth: 140, opacity: saving ? 0.7 : 1 }}>
        {saving ? '⟳ Saving…' : saveLabel}
      </button>
    </div>
  );
}
const _iStyle = { ...inputStyle, borderRadius: 8, padding: '10px 12px', fontSize: 14 };
const _iStyleErr = { ..._iStyle, borderColor: '#ef4444', background: 'rgba(239,68,68,0.04)' };
function FInput({ label, required, hint, span, value, onChange, placeholder, type = 'text', min, step, autoFocus, error }) {
  return (
    <div style={{ gridColumn: span === 2 ? '1/-1' : undefined }}>
      <Field label={label} required={required} hint={hint} error={error}>
        <input type={type} value={value || ''} onChange={onChange} placeholder={placeholder}
          min={min} step={step} autoFocus={autoFocus} style={error ? _iStyleErr : _iStyle} />
      </Field>
    </div>
  );
}
function FSelect({ label, required, hint, span, value, onChange, children, error }) {
  return (
    <div style={{ gridColumn: span === 2 ? '1/-1' : undefined }}>
      <Field label={label} required={required} hint={hint} error={error}>
        <select value={value || ''} onChange={onChange} style={{ ...((error ? _iStyleErr : _iStyle)), cursor: 'pointer' }}>
          {children}
        </select>
      </Field>
    </div>
  );
}
function ErrorBanner({ errors }) {
  const general = errors?._general || errors?.non_field_errors || errors?.detail;
  if (!general) return null;
  return (
    <div style={{
      margin: '0 0 16px', padding: '10px 14px', borderRadius: 8,
      background: 'rgba(239,68,68,0.07)', border: '1.5px solid rgba(239,68,68,0.25)',
      display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, color: '#ef4444',
    }}>
      <span style={{ fontSize: 15, flexShrink: 0 }}>⚠️</span>
      <span>{general}</span>
    </div>
  );
}

// ─── Stock Catalogue ──────────────────────────────────────────────────────────
function StockCatalogue({ items, notify, onUpdate }) {
  const [showForm, setShowForm]  = useState(false);
  const [editItem, setEditItem]  = useState(null);
  const [catFilter, setCatFilter] = useState('all');
  const [search, setSearch]      = useState('');
  const [histItem, setHistItem]  = useState(null);
  const [hist, setHist]          = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving]      = useState(false);
  const blank = { name:'', category:'other', unit:'piece', current_stock:'', reorder_level:'', unit_cost:'', supplier:'', description:'' };
  const [form, setForm]          = useState(blank);

  const sfi = (k, v) => { setForm(p => ({...p,[k]:v})); setFormErrors(p => ({...p,[k]:''})); };

  const save = async () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Item name is required';
    if (!form.unit)         errs.unit = 'Unit is required';
    if (form.unit_cost && (isNaN(Number(form.unit_cost)) || Number(form.unit_cost) < 0)) errs.unit_cost = 'Enter a valid cost';
    if (form.reorder_level && isNaN(Number(form.reorder_level))) errs.reorder_level = 'Must be a number';
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    setFormErrors({});
    setSaving(true);
    try {
      if (editItem) {
        await api.updateStockItem(editItem.id, form);
        notify('Item updated!');
      } else {
        await api.createStockItem(form);
        notify('Stock item created!');
      }
      setShowForm(false); setEditItem(null); setForm(blank);
      onUpdate();
    } catch(e) {
      const data = e.response?.data;
      if (data && typeof data === 'object') {
        const mapped = {};
        Object.entries(data).forEach(([k,v]) => { mapped[k] = Array.isArray(v) ? v[0] : String(v); });
        setFormErrors(mapped);
        notify('Please fix the highlighted fields', 'error');
      } else { notify(data || 'Error saving item', 'error'); }
    } finally { setSaving(false); }
  };

  const filtered = items.filter(i =>
    (catFilter === 'all' || i.category === catFilter) &&
    (!search || i.name.toLowerCase().includes(search.toLowerCase()))
  );

  const startEdit = (item) => {
    setEditItem(item);
    setFormErrors({});
    setForm({ name:item.name, category:item.category, unit:item.unit,
              current_stock:item.current_stock, reorder_level:item.reorder_level,
              unit_cost:item.unit_cost, supplier:item.supplier||'', description:item.description||'' });
    setShowForm(true);
  };

  const viewHistory = async (item) => {
    setHistItem(item);
    const r = await api.getStockHistory(item.id);
    setHist(r.data);
  };

  return (
    <>
      {/* History modal */}
      {histItem && (
        <div style={{ position:'fixed',inset:0,background:'var(--bgModal)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
          <div style={{ background:'var(--bgCard)',border:'1px solid var(--border)',borderRadius:6,padding:24,width:'100%',maxWidth:600,maxHeight:'80vh',overflowY:'auto' }}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16 }}>
              <div style={{ fontWeight:700,color:'var(--textPrimary)',fontSize:16 }}>📜 {histItem.name} — Movement History</div>
              <button onClick={() => setHistItem(null)} style={{ background:'none',border:'none',color:'var(--textFaint)',fontSize:20,cursor:'pointer' }}>✕</button>
            </div>
            {hist.length === 0 ? <Empty text="No movements recorded yet" /> :
              <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                <thead><tr style={{ background:'var(--bgCard)' }}>
                  {['Date','Type','Before','Change','After','By','Reason'].map(h =>
                    <th key={h} style={{ padding:'6px 8px',textAlign:'left',color:'#c8a96e',fontWeight:700,fontSize:10,letterSpacing:1 }}>{h}</th>)}
                </tr></thead>
                <tbody>{hist.map(h => (
                  <tr key={h.id} style={{ borderBottom:'1px solid #1a1a1a' }}>
                    <td style={{ padding:'5px 8px',color:'var(--textMuted)',fontSize:10 }}>{new Date(h.created_at).toLocaleDateString('en-KE')}</td>
                    <td style={{ padding:'5px 8px',color:'#c8a96e',fontSize:10,textTransform:'capitalize' }}>{h.adj_type}</td>
                    <td style={{ padding:'5px 8px',color:'var(--textMuted)' }}>{h.qty_before}</td>
                    <td style={{ padding:'5px 8px',color:h.qty_change>=0?'#2ecc71':'#e74c3c',fontWeight:700 }}>{h.qty_change>=0?'+':''}{h.qty_change}</td>
                    <td style={{ padding:'5px 8px',color:'var(--textPrimary)',fontWeight:600 }}>{h.qty_after}</td>
                    <td style={{ padding:'5px 8px',color:'var(--textMuted)',fontSize:10 }}>{h.adjusted_by_name}</td>
                    <td style={{ padding:'5px 8px',color:'var(--textFaint)',fontSize:10 }}>{h.reason||'—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            }
          </div>
        </div>
      )}

      <Card title="Stock Catalogue">
        <div style={{ display:'flex',gap:10,marginBottom:14,flexWrap:'wrap',alignItems:'center' }}>
          <input style={{ ...inputStyle,width:200,padding:'7px 10px' }} placeholder="Search items…"
            value={search} onChange={e => setSearch(e.target.value)} />
          <select style={{ ...inputStyle,width:'auto' }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            <option value="all">All Categories</option>
            {CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
          <div style={{ marginLeft:'auto' }}>
            <button style={btnPrimary} onClick={() => { setEditItem(null); setForm(blank); setFormErrors({}); setShowForm(true); }}>
              ＋ Add Stock Item
            </button>
          </div>
        </div>

        {showForm && (
          <Modal onClose={() => { setShowForm(false); setEditItem(null); setFormErrors({}); }} width={640}>
            <ModalHeader
              icon={editItem ? '✏️' : '📦'}
              title={editItem ? `Edit — ${editItem.name}` : 'New Stock Item'}
              subtitle={editItem ? 'Update the details for this stock item' : 'Add a new item to the stock catalogue'}
              onClose={() => { setShowForm(false); setEditItem(null); setFormErrors({}); }}
            />
            <div style={{ padding: '20px 24px' }}>
              <ErrorBanner errors={formErrors} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FInput label="Item Name" required span={2} autoFocus
                  value={form.name} onChange={e => sfi('name', e.target.value)}
                  placeholder="e.g. Rice 25kg bag" error={formErrors.name} />
                <FSelect label="Category" value={form.category} onChange={e => sfi('category', e.target.value)}>
                  {CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
                </FSelect>
                <FSelect label="Unit" required value={form.unit} onChange={e => sfi('unit', e.target.value)} error={formErrors.unit}>
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </FSelect>
                <FInput label="Opening Stock" type="number"
                  value={form.current_stock} onChange={e => sfi('current_stock', e.target.value)} placeholder="0" />
                <FInput label="Reorder Level" type="number"
                  value={form.reorder_level} onChange={e => sfi('reorder_level', e.target.value)} placeholder="0" error={formErrors.reorder_level} />
                <FInput label="Unit Cost (KES)" type="number" min="0" step="0.01"
                  value={form.unit_cost} onChange={e => sfi('unit_cost', e.target.value)} placeholder="0.00" error={formErrors.unit_cost} />
                <FInput label="Supplier"
                  value={form.supplier} onChange={e => sfi('supplier', e.target.value)} placeholder="Supplier name" />
              </div>
            </div>
            <ModalFooter
              onSave={save} onCancel={() => { setShowForm(false); setEditItem(null); setFormErrors({}); }}
              saveLabel={editItem ? '✓ Save Changes' : '✓ Create Item'} saving={saving}
              errorCount={Object.values(formErrors).filter(Boolean).length}
            />
          </Modal>
        )}

        {filtered.length === 0 ? <Empty text="No stock items found" /> :
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
              <thead><tr style={{ background:'var(--bgInput)' }}>
                {['Item','Category','Unit','In Stock','Reorder Lvl','Unit Cost','Stock Value','Status','Actions'].map(h =>
                  <th key={h} style={{ padding:'8px 10px',textAlign:'left',color:'var(--textSecond)',fontSize:10,letterSpacing:1,textTransform:'uppercase',whiteSpace:'nowrap',borderBottom:'2px solid var(--border)',fontWeight:700 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id} style={{ borderBottom:'1px solid var(--border)',background:item.is_low_stock?'rgba(231,76,60,0.05)':'transparent' }}>
                    <td style={{ padding:'8px 10px',fontWeight:600,color:'var(--textPrimary)' }}>
                      {item.is_low_stock && <span style={{ color:'#e74c3c',marginRight:4 }}>⚠</span>}
                      {item.name}
                    </td>
                    <td style={{ padding:'8px 10px' }}>
                      <span style={{ background:CAT_COLORS[item.category]+'22',color:CAT_COLORS[item.category],padding:'2px 8px',borderRadius:3,fontSize:10,fontWeight:700 }}>
                        {CATEGORIES.find(c=>c.v===item.category)?.label || item.category}
                      </span>
                    </td>
                    <td style={{ padding:'8px 10px',color:'var(--textSecond)' }}>{item.unit}</td>
                    <td style={{ padding:'8px 10px',fontWeight:700,color:item.is_low_stock?'#e74c3c':'#2ecc71',fontSize:14 }}>{parseFloat(item.current_stock)}</td>
                    <td style={{ padding:'8px 10px',color:'var(--textMuted)' }}>{parseFloat(item.reorder_level)}</td>
                    <td style={{ padding:'8px 10px',color:'var(--textSecond)' }}>{fmtKES(item.unit_cost)}</td>
                    <td style={{ padding:'8px 10px',color:'#c8a96e',fontWeight:600 }}>{fmtKES(item.stock_value)}</td>
                    <td style={{ padding:'8px 10px' }}>
                      {item.is_low_stock
                        ? <span style={{ background:'#e74c3c22',color:'#e74c3c',padding:'2px 8px',borderRadius:3,fontSize:10,fontWeight:700 }}>LOW STOCK</span>
                        : <span style={{ background:'#2ecc7122',color:'#2ecc71',padding:'2px 8px',borderRadius:3,fontSize:10,fontWeight:700 }}>OK</span>}
                    </td>
                    <td style={{ padding:'8px 10px' }}>
                      <div style={{ display:'flex',gap:4 }}>
                        <button style={{ ...btnSm,fontSize:10 }} onClick={() => startEdit(item)}>Edit</button>
                        <button className="btn btn-default btn-sm" onClick={() => viewHistory(item)}>History</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </Card>
    </>
  );
}


// ─── Stock Adjustment Panel ───────────────────────────────────────────────────
function StockAdjustPanel({ items, notify, onUpdate }) {
  const bp = useBreakpoint();
  const [selectedItem, setSelectedItem] = useState('');
  const [adjType, setAdjType]   = useState('receive');
  const [qtyChange, setQtyChange] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [reason, setReason]     = useState('');
  const [reference, setRef]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');

  const item = items.find(i => i.id === parseInt(selectedItem));
  const actualQty = adjType === 'writeoff' || adjType === 'return'
    ? -Math.abs(parseFloat(qtyChange)||0)
    : parseFloat(qtyChange)||0;
  const newQty = item ? parseFloat(item.current_stock) + actualQty : null;

  const submit = async () => {
    if (!selectedItem) { notify('Select an item', 'error'); return; }
    if (!qtyChange || qtyChange === '0') { notify('Enter a quantity', 'error'); return; }
    setLoading(true);
    try {
      await api.adjustStock(selectedItem, {
        adj_type: adjType,
        qty_change: actualQty,
        unit_cost: unitCost || item?.unit_cost || 0,
        reason,
        reference,
      });
      notify(`Stock adjusted successfully!`);
      setSelectedItem(''); setQtyChange(''); setReason(''); setRef(''); setUnitCost('');
      onUpdate();
    } catch(e) { notify(e.response?.data?.error || 'Error adjusting stock', 'error'); }
    finally { setLoading(false); }
  };

  const filteredItems = items.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ display:'grid',gridTemplateColumns: bp === 'sm' ? '1fr' : '1fr 340px',gap:16,alignItems:'start' }}>
      {/* Item selector */}
      <Card title="Select Item to Adjust">
        <input style={{ ...inputStyle,marginBottom:10 }} placeholder="Search items…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ maxHeight:500,overflowY:'auto',display:'grid',gap:6 }}>
          {filteredItems.map(i => (
            <div key={i.id}
              onClick={() => { setSelectedItem(String(i.id)); setUnitCost(i.unit_cost); }}
              style={{ background:selectedItem===String(i.id)?'#1a1a0a':'var(--bgInput)',
                       border:`2px solid ${selectedItem===String(i.id)?'#c8a96e':i.is_low_stock?'#e74c3c44':'var(--border)'}`,
                       borderRadius:4,padding:'10px 12px',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <div>
                <div style={{ fontWeight:600,color:'var(--textPrimary)',fontSize:13 }}>
                  {i.is_low_stock && <span style={{ color:'#e74c3c',marginRight:4 }}>⚠</span>}
                  {i.name}
                </div>
                <div style={{ fontSize:10,color:'var(--textMuted)',marginTop:2 }}>
                  {CATEGORIES.find(c=>c.v===i.category)?.label} · {i.unit}
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontWeight:700,fontSize:15,color:i.is_low_stock?'#e74c3c':'#2ecc71' }}>{parseFloat(i.current_stock)}</div>
                <div style={{ fontSize:10,color:'var(--textMuted)' }}>{i.unit}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Adjustment form */}
      <Card title="Adjustment Details">
        {!item ? (
          <div style={{ color:'var(--textMuted)',fontSize:13,padding:'20px 0',textAlign:'center' }}>← Select an item to adjust</div>
        ) : (
          <>
            <div style={{ background:'#0e0e0e',borderRadius:4,padding:'10px 12px',marginBottom:14 }}>
              <div style={{ fontWeight:700,color:'var(--textPrimary)',fontSize:14 }}>{item.name}</div>
              <div style={{ fontSize:12,color:'var(--textMuted)',marginTop:2 }}>
                Current stock: <strong style={{ color:'#c8a96e',fontSize:16 }}>{parseFloat(item.current_stock)} {item.unit}</strong>
              </div>
            </div>

            <Field label="Adjustment Type">
              <select style={inputStyle} value={adjType} onChange={e => setAdjType(e.target.value)}>
                {ADJ_TYPES.map(a => <option key={a.v} value={a.v}>{a.label}</option>)}
              </select>
            </Field>

            <Field label={adjType==='writeoff'||adjType==='return' ? 'Quantity to Remove' : 'Quantity'}>
              <input style={{ ...inputStyle,fontSize:18,fontWeight:700,color:adjType==='writeoff'||adjType==='return'?'#e74c3c':'#2ecc71' }}
                type="number" min="0" step="0.001" value={qtyChange}
                onChange={e => setQtyChange(e.target.value)} placeholder="0" />
            </Field>

            {adjType === 'receive' && (
              <Field label="Unit Cost (KES)">
                <input style={inputStyle} type="number" value={unitCost}
                  onChange={e => setUnitCost(e.target.value)} placeholder={item.unit_cost} />
              </Field>
            )}

            {newQty !== null && qtyChange && (
              <div style={{ background:newQty<0?'#2a0a0a':newQty<=parseFloat(item.reorder_level)?'#1a1400':'#0a1a0a',
                            border:`1px solid ${newQty<0?'#e74c3c':newQty<=parseFloat(item.reorder_level)?'#e67e22':'#2ecc71'}`,
                            borderRadius:4,padding:'8px 12px',marginBottom:10 }}>
                <div style={{ fontSize:11,color:'var(--textMuted)' }}>New stock after adjustment:</div>
                <div style={{ fontSize:22,fontWeight:700,color:newQty<0?'#e74c3c':newQty<=parseFloat(item.reorder_level)?'#e67e22':'#2ecc71' }}>
                  {newQty.toFixed(3)} {item.unit}
                </div>
                {newQty < 0 && <div style={{ color:'#e74c3c',fontSize:11,marginTop:2 }}>⚠ Cannot go below zero</div>}
                {newQty >= 0 && newQty <= parseFloat(item.reorder_level) && <div style={{ color:'#e67e22',fontSize:11,marginTop:2 }}>⚠ Will be below reorder level</div>}
              </div>
            )}

            <Field label="Reason / Notes">
              <textarea style={{ ...inputStyle,height:60,resize:'none' }} value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder={adjType==='receive'?'Delivery note description…':adjType==='writeoff'?'What was damaged/lost?':'Reason for adjustment…'} />
            </Field>

            <Field label="Reference (LPO/DN No.)">
              <input style={inputStyle} value={reference}
                onChange={e => setRef(e.target.value)} placeholder="e.g. LPO-2024-055" />
            </Field>

            <button style={{ ...btnPrimary,background:adjType==='writeoff'||adjType==='return'?'#c0392b':'#c8a96e',
                             color:'#111',opacity:(newQty!==null&&newQty<0)?0.4:1 }}
              disabled={loading||(newQty!==null&&newQty<0)} onClick={submit}>
              {loading ? 'Saving…' : adjType==='receive'?'📦 Receive Stock':adjType==='count'?'🔢 Apply Count':adjType==='writeoff'?'🗑️ Write Off':'✓ Apply Adjustment'}
            </button>
          </>
        )}
      </Card>
    </div>
  );
}


// ─── Issuance Panel ───────────────────────────────────────────────────────────
function IssuancePanel({ items, issuances, staffUsers, notify, onUpdate }) {
  const [mode, setMode]     = useState('list'); // 'list' | 'new' | 'view' | 'report'
  const [current, setCurrent] = useState(null);
  // Filters
  const [fDept,   setFDept]   = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fFrom,   setFFrom]   = useState('');
  const [fTo,     setFTo]     = useState('');
  const [fSearch, setFSearch] = useState('');

  if (mode === 'new')
    return <NewIssuanceForm items={items} staffUsers={staffUsers} notify={notify} onUpdate={onUpdate} onBack={() => setMode('list')} />;
  if (mode === 'view' && current)
    return <IssuanceDetail issuance={current} notify={notify}
             onUpdate={() => { onUpdate(); setMode('list'); }} onBack={() => setMode('list')} />;
  if (mode === 'report')
    return <IssuanceReport onBack={() => setMode('list')} notify={notify} />;

  // Apply client-side filters
  const filtered = issuances.filter(iss => {
    if (fDept   && iss.department !== fDept)   return false;
    if (fStatus && iss.status     !== fStatus)  return false;
    if (fSearch && !iss.ref.toLowerCase().includes(fSearch.toLowerCase()) &&
        !(iss.requested_by||'').toLowerCase().includes(fSearch.toLowerCase())) return false;
    if (fFrom) {
      const d = iss.issued_at || iss.created_at;
      if (d && d.slice(0,10) < fFrom) return false;
    }
    if (fTo) {
      const d = iss.issued_at || iss.created_at;
      if (d && d.slice(0,10) > fTo) return false;
    }
    return true;
  });

  const totalValue = filtered
    .filter(i => i.status === 'issued')
    .reduce((s,i) => s + parseFloat(i.total_value||0), 0);

  const statusColor = s => s==='issued'?'#2ecc71':s==='cancelled'?'#e74c3c':'#c8a96e';

  return (
    <div>
      {/* Action bar */}
      <div style={{ display:'flex',gap:10,marginBottom:14,flexWrap:'wrap',alignItems:'center' }}>
        <button style={btnPrimary} onClick={() => setMode('new')}>＋ New Issuance</button>
        <button style={{ ...btnSm,background:'#1a2a3a',color:'#3498db',border:'1px solid #3498db44',padding:'7px 16px',fontSize:12 }}
          onClick={() => setMode('report')}>📊 Reports &amp; Export</button>
        <div style={{ marginLeft:'auto',fontSize:12,color:'var(--textMuted)' }}>
          Showing <strong style={{ color:'#c8a96e' }}>{filtered.length}</strong> / {issuances.length} vouchers
          {filtered.some(i=>i.status==='issued') && <span> · Issued value: <strong style={{ color:'#2ecc71' }}>{fmtKES(totalValue)}</strong></span>}
        </div>
      </div>

      {/* Filters */}
      <div style={{ background:'var(--bgInput)',border:'1px solid var(--border)',borderRadius:6,
                    padding:'12px 14px',marginBottom:14,display:'grid',
                    gridTemplateColumns:'1fr 1fr 1fr 160px 160px',gap:10 }}>
        <div>
          <div style={{ fontSize:10,color:'var(--textMuted)',marginBottom:4,letterSpacing:1,textTransform:'uppercase' }}>Search</div>
          <input style={{ ...inputStyle,padding:'6px 10px' }} placeholder="Ref or requester…"
            value={fSearch} onChange={e => setFSearch(e.target.value)} />
        </div>
        <div>
          <div style={{ fontSize:10,color:'var(--textMuted)',marginBottom:4,letterSpacing:1,textTransform:'uppercase' }}>Department</div>
          <select style={{ ...inputStyle,padding:'6px 10px' }} value={fDept} onChange={e => setFDept(e.target.value)}>
            <option value="">All Departments</option>
            {DEPARTMENTS.map(d => <option key={d} value={d}>{DEPT_LABELS[d]}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize:10,color:'var(--textMuted)',marginBottom:4,letterSpacing:1,textTransform:'uppercase' }}>Status</div>
          <select style={{ ...inputStyle,padding:'6px 10px' }} value={fStatus} onChange={e => setFStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize:10,color:'var(--textMuted)',marginBottom:4,letterSpacing:1,textTransform:'uppercase' }}>From</div>
          <input type="date" style={{ ...inputStyle,padding:'6px 10px' }} value={fFrom} onChange={e => setFFrom(e.target.value)} />
        </div>
        <div>
          <div style={{ fontSize:10,color:'var(--textMuted)',marginBottom:4,letterSpacing:1,textTransform:'uppercase' }}>To</div>
          <input type="date" style={{ ...inputStyle,padding:'6px 10px' }} value={fTo} onChange={e => setFTo(e.target.value)} />
        </div>
      </div>
      {(fDept||fStatus||fFrom||fTo||fSearch) && (
        <div style={{ marginBottom:10,display:'flex',gap:8,alignItems:'center' }}>
          <span style={{ fontSize:11,color:'#c8a96e' }}>Filters active</span>
          <button style={{ ...btnSm,fontSize:10,padding:'3px 10px',background:'#1a1a1a' }}
            onClick={() => { setFDept(''); setFStatus(''); setFFrom(''); setFTo(''); setFSearch(''); }}>
            ✕ Clear Filters
          </button>
        </div>
      )}

      {/* List */}
      {filtered.length === 0 ? <Empty text="No issuances match your filters" /> :
        <div style={{ display:'grid',gap:8 }}>
          {filtered.map(iss => {
            const sc = statusColor(iss.status);
            return (
              <div key={iss.id}
                style={{ background:'var(--bgInput)',border:`1px solid ${sc}33`,
                         borderLeft:`4px solid ${sc}`,borderRadius:4,padding:'12px 14px',
                         display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10 }}>
                <div>
                  <div style={{ fontWeight:700,color:'var(--textPrimary)',fontSize:14,letterSpacing:0.5 }}>{iss.ref}</div>
                  <div style={{ fontSize:12,color:'var(--textSecond)',marginTop:2 }}>
                    → <strong>{DEPT_LABELS[iss.department]||iss.department}</strong>
                    {iss.requested_by && <span style={{ color:'var(--textMuted)' }}> · Req: {iss.requested_by}</span>}
                    {iss.purpose && <span style={{ color:'var(--textMuted)' }}> · {iss.purpose}</span>}
                  </div>
                  <div style={{ fontSize:11,color:'var(--textMuted)',marginTop:3,display:'flex',gap:12 }}>
                    <span>📦 {iss.issuance_items?.length||0} items</span>
                    <span style={{ color:'#c8a96e' }}>{fmtKES(iss.total_value)}</span>
                    {iss.issued_at && <span>🕐 {new Date(iss.issued_at).toLocaleDateString('en-KE')}</span>}
                    {iss.issued_by_name && <span>👤 {iss.issued_by_name}</span>}
                  </div>
                </div>
                <div style={{ display:'flex',gap:8,alignItems:'center' }}>
                  <span style={{ padding:'3px 10px',borderRadius:3,fontSize:10,fontWeight:700,
                                 background:`${sc}22`,color:sc,textTransform:'uppercase',letterSpacing:1 }}>
                    {iss.status}
                  </span>
                  <button style={{ ...btnSm,fontSize:11 }} onClick={() => { setCurrent(iss); setMode('view'); }}>
                    {iss.status==='draft'?'📝 Open':'👁 View'}
                  </button>
                  {iss.status==='issued' && (
                    <button style={{ ...btnSm,fontSize:11,background:'#0a1a0a',color:'#2ecc71',border:'1px solid #2ecc7133' }}
                      onClick={() => printIssuanceVoucher(iss)}>🖨 Print</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      }
    </div>
  );
}

/* ── Issuance Report & Export Panel ─────────────────────────────────────────── */
function IssuanceReport({ onBack, notify }) {
  const [fDept,   setFDept]   = useState('');
  const [fStatus, setFStatus] = useState('issued');
  const [fFrom,   setFFrom]   = useState('');
  const [fTo,     setFTo]     = useState('');
  const [issuances, setIssuances] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('gm_authtoken');

  const buildParams = () => {
    const p = new URLSearchParams();
    if (fDept)   p.set('department', fDept);
    if (fStatus) p.set('status', fStatus);
    if (fFrom)   p.set('date_from', fFrom);
    if (fTo)     p.set('date_to', fTo);
    return p.toString() ? `?${p.toString()}` : '';
  };

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await api.getIssuances(buildParams());
      setIssuances(res.data.results || res.data);
    } catch { notify('Failed to load issuances', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPreview(); }, []); // eslint-disable-line

  const downloadFile = async (type) => {
    const params = buildParams();
    const url = type === 'excel'
      ? api.exportIssuanceExcel(params)
      : api.exportIssuancePdf(params);
    try {
      const res2 = await axiosClient.get(url, { responseType: 'blob', baseURL: '' });
      // res2.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res2.data;
      if (blob.size < 50) throw new Error('Empty file — check server logs');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${getBrandingSlug()}_Issuance_${new Date().toISOString().slice(0,10)}.${type === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link); link.click();
      setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(link.href); }, 1000);
      notify(`Issuance report downloaded ✓`);
    } catch(e) { notify(`Export failed: ${await readBlobError(e)}`, 'error'); }
  };

  const totalVal = issuances.filter(i=>i.status==='issued').reduce((s,i)=>s+parseFloat(i.total_value||0),0);
  const deptGroups = issuances.reduce((acc,i) => {
    if (i.status!=='issued') return acc;
    acc[i.department] = (acc[i.department]||0) + parseFloat(i.total_value||0);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display:'flex',gap:10,alignItems:'center',marginBottom:16 }}>
        <button style={btnSm} onClick={onBack}>← Back</button>
        <div style={{ fontWeight:700,color:'var(--textPrimary)',fontSize:16 }}>📊 Issuance Reports &amp; Export</div>
      </div>

      {/* Filter Panel */}
      <Card title="Filter Report">
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:12,marginBottom:14 }}>
          <div>
            <div style={{ fontSize:10,color:'var(--textMuted)',marginBottom:4,letterSpacing:1,textTransform:'uppercase' }}>Department</div>
            <select style={inputStyle} value={fDept} onChange={e => setFDept(e.target.value)}>
              <option value="">All Departments</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{DEPT_LABELS[d]}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize:10,color:'var(--textMuted)',marginBottom:4,letterSpacing:1,textTransform:'uppercase' }}>Status</div>
            <select style={inputStyle} value={fStatus} onChange={e => setFStatus(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="issued">Issued</option>
              <option value="draft">Draft</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize:10,color:'var(--textMuted)',marginBottom:4,letterSpacing:1,textTransform:'uppercase' }}>Date From</div>
            <input type="date" style={inputStyle} value={fFrom} onChange={e => setFFrom(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize:10,color:'var(--textMuted)',marginBottom:4,letterSpacing:1,textTransform:'uppercase' }}>Date To</div>
            <input type="date" style={inputStyle} value={fTo} onChange={e => setFTo(e.target.value)} />
          </div>
        </div>
        <div style={{ display:'flex',gap:10 }}>
          <button style={{ ...btnPrimary,flex:1 }} onClick={loadPreview} disabled={loading}>
            {loading ? '⏳ Loading…' : '🔍 Preview Report'}
          </button>
          <button style={{ ...btnSm,flex:1,background:'#0a1a2a',color:'#3498db',border:'1px solid #3498db44',fontSize:12,padding:'8px' }}
            onClick={() => downloadFile('excel')}>
            📥 Export Excel
          </button>
          <button style={{ ...btnSm,flex:1,background:'#1a0a0a',color:'#e74c3c',border:'1px solid #e74c3c44',fontSize:12,padding:'8px' }}
            onClick={() => downloadFile('pdf')}>
            📄 Export PDF
          </button>
        </div>
      </Card>

      {/* Summary Stats */}
      {issuances.length > 0 && (
        <>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12,margin:'14px 0' }}>
            {[
              { label:'Total Vouchers',    value:issuances.length,                     color:'#3498db' },
              { label:'Issued',            value:issuances.filter(i=>i.status==='issued').length,    color:'#2ecc71' },
              { label:'Draft / Pending',   value:issuances.filter(i=>i.status==='draft').length,     color:'#c8a96e' },
              { label:'Total Value Issued',value:fmtKES(totalVal),                     color:'#c8a96e' },
            ].map(s => (
              <div key={s.label} style={{ background:'var(--bgInput)',border:`1px solid ${s.color}33`,
                                          borderTop:`3px solid ${s.color}`,borderRadius:6,padding:'14px 16px',textAlign:'center' }}>
                <div style={{ fontSize:20,fontWeight:800,color:s.color }}>{s.value}</div>
                <div style={{ fontSize:11,color:'var(--textMuted)',marginTop:3 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Department Breakdown */}
          {Object.keys(deptGroups).length > 0 && (
            <Card title="Value by Department">
              <div style={{ display:'grid',gap:6 }}>
                {Object.entries(deptGroups).sort((a,b)=>b[1]-a[1]).map(([dept,val]) => {
                  const maxVal = Math.max(...Object.values(deptGroups));
                  const pct = (val/maxVal*100).toFixed(0);
                  return (
                    <div key={dept} style={{ display:'flex',alignItems:'center',gap:10 }}>
                      <div style={{ width:140,fontSize:12,color:'var(--textSecond)',flexShrink:0 }}>
                        {DEPT_LABELS[dept]||dept}
                      </div>
                      <div style={{ flex:1,height:14,background:'#1a1a1a',borderRadius:3,overflow:'hidden' }}>
                        <div style={{ width:`${pct}%`,height:'100%',background:'linear-gradient(90deg,#c8a96e,#f0c87a)',borderRadius:3,
                                      transition:'width 0.5s ease' }} />
                      </div>
                      <div style={{ width:110,textAlign:'right',fontSize:12,color:'#c8a96e',fontWeight:700,flexShrink:0 }}>
                        {fmtKES(val)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Detail Table */}
          <Card title={`Issuances (${issuances.length})`}>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                <thead>
                  <tr style={{ background:'var(--bgInput)' }}>
                    {['Ref','Department','Requested By','Items','Total Value','Status','Issued At','Issued By','Action'].map(h =>
                      <th key={h} style={{ padding:'8px 10px',textAlign:'left',color:'var(--textSecond)',fontSize:10,
                                           letterSpacing:1,fontWeight:700,borderBottom:'2px solid var(--border)',whiteSpace:'nowrap' }}>{h}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {issuances.map(iss => {
                    const sc = iss.status==='issued'?'#2ecc71':iss.status==='cancelled'?'#e74c3c':'#c8a96e';
                    return (
                      <tr key={iss.id} style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={{ padding:'8px 10px',fontWeight:700,color:'#c8a96e' }}>{iss.ref}</td>
                        <td style={{ padding:'8px 10px',color:'var(--textSecond)' }}>{DEPT_LABELS[iss.department]||iss.department}</td>
                        <td style={{ padding:'8px 10px',color:'var(--textMuted)' }}>{iss.requested_by||'—'}</td>
                        <td style={{ padding:'8px 10px',color:'var(--textMuted)',textAlign:'center' }}>{iss.issuance_items?.length||0}</td>
                        <td style={{ padding:'8px 10px',color:'#c8a96e',fontWeight:600 }}>{fmtKES(iss.total_value)}</td>
                        <td style={{ padding:'8px 10px' }}>
                          <span style={{ padding:'2px 8px',borderRadius:3,fontSize:10,fontWeight:700,
                                         background:`${sc}22`,color:sc,textTransform:'uppercase' }}>{iss.status}</span>
                        </td>
                        <td style={{ padding:'8px 10px',color:'var(--textMuted)',whiteSpace:'nowrap' }}>
                          {iss.issued_at ? new Date(iss.issued_at).toLocaleDateString('en-KE') : '—'}
                        </td>
                        <td style={{ padding:'8px 10px',color:'var(--textMuted)' }}>{iss.issued_by_name||'—'}</td>
                        <td style={{ padding:'8px 10px' }}>
                          {iss.status==='issued' && (
                            <button style={{ ...btnSm,fontSize:10,background:'#0a1a0a',color:'#2ecc71',padding:'3px 8px' }}
                              onClick={() => printIssuanceVoucher(iss)}>🖨 Print</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background:'var(--bgInput)' }}>
                    <td colSpan={4} style={{ padding:'8px 10px',fontWeight:700,color:'var(--textPrimary)',textAlign:'right' }}>
                      TOTAL ISSUED VALUE
                    </td>
                    <td style={{ padding:'8px 10px',fontWeight:700,color:'#c8a96e',fontSize:14 }}>{fmtKES(totalVal)}</td>
                    <td colSpan={4} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function NewIssuanceForm({ items, staffUsers, notify, onUpdate, onBack }) {
  const bp = useBreakpoint();
  const [dept, setDept]       = useState('');
  const [reqBy, setReqBy]     = useState('');
  const [reqSearch, setReqSearch] = useState('');
  const [showStaffDrop, setShowStaffDrop] = useState(false);
  const [purpose, setPurpose] = useState('');
  const [cart, setCart]       = useState([]);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(false);

  const ROLE_LABELS = {
    admin:'Admin', reception:'Reception', cashier:'Cashier', waiter:'Waiter',
    kitchen:'Kitchen', chef:'Chef', housekeeping:'Housekeeping', gate:'Gate',
    accountant:'Accountant', gift_shop:'Gift Shop', restaurant_manager:'Mgr',
    storekeeper:'Storekeeper', hr:'HR', marketing:'Marketing',
  };

  const filteredStaff = staffUsers.filter(s =>
    s.is_active && (!reqSearch || s.name.toLowerCase().includes(reqSearch.toLowerCase()))
  );

  const selectStaff = (s) => {
    setReqBy(`${s.name} — ${ROLE_LABELS[s.role] || s.role}`);
    setReqSearch(s.name);
    setShowStaffDrop(false);
  };

  const addToCart = (item) => {
    if (cart.find(c => c.item_id === item.id)) return;
    setCart(c => [...c, { item_id:item.id, name:item.name, unit:item.unit,
                          available:parseFloat(item.current_stock), qty_issued:'', unit_cost:item.unit_cost }]);
  };
  const removeFromCart = (id) => setCart(c => c.filter(i => i.item_id !== id));
  const setQty = (id, v) => setCart(c => c.map(i => i.item_id===id ? {...i,qty_issued:v} : i));

  const submit = async () => {
    if (!dept) { notify('Select a department', 'error'); return; }
    if (cart.length === 0) { notify('Add at least one item', 'error'); return; }
    const invalid = cart.filter(c => !c.qty_issued || parseFloat(c.qty_issued) <= 0);
    if (invalid.length > 0) { notify(`Enter quantity for: ${invalid.map(i=>i.name).join(', ')}`, 'error'); return; }
    const overstock = cart.filter(c => parseFloat(c.qty_issued) > c.available);
    if (overstock.length > 0) { notify(`Insufficient stock for: ${overstock.map(i=>i.name).join(', ')}`, 'error'); return; }

    setLoading(true);
    try {
      // Create draft issuance
      const res = await api.createIssuance({ department:dept, requested_by:reqBy, purpose, status:'draft' });
      // Finalise with items
      const issued = await api.issueVoucher(res.data.id, {
        items: cart.map(c => ({ item_id:c.item_id, qty_issued:parseFloat(c.qty_issued) }))
      });
      printIssuanceVoucher(issued.data);
      notify(`Issuance ${issued.data.ref} completed! Voucher printed.`);
      onUpdate();
      onBack();
    } catch(e) { notify(e.response?.data?.error || 'Error creating issuance', 'error'); }
    finally { setLoading(false); }
  };

  const filteredItems = items.filter(i => i.is_active &&
    (!search || i.name.toLowerCase().includes(search.toLowerCase())));

  return (
    <div style={{ display:'grid',gridTemplateColumns: bp === 'sm' ? '1fr' : '1fr 380px',gap:16,alignItems:'start' }}>
      <Card title="Select Items to Issue">
        <input style={{ ...inputStyle,marginBottom:10 }} placeholder="Search stock items…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ maxHeight:520,overflowY:'auto',display:'grid',gap:6 }}>
          {filteredItems.map(i => {
            const inCart = cart.some(c => c.item_id === i.id);
            return (
              <div key={i.id}
                style={{ background:inCart?'#1a1a0a':'var(--bgInput)',
                         border:`1px solid ${inCart?'#c8a96e':i.is_low_stock?'#e74c3c44':'var(--border)'}`,
                         borderRadius:4,padding:'10px 12px',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:600,color:'var(--textPrimary)',fontSize:13 }}>{i.name}</div>
                  <div style={{ fontSize:10,color:'var(--textMuted)',marginTop:2 }}>
                    {CATEGORIES.find(c=>c.v===i.category)?.label} · Available: <strong style={{ color:i.is_low_stock?'#e74c3c':'#2ecc71' }}>{parseFloat(i.current_stock)} {i.unit}</strong>
                  </div>
                </div>
                <button style={{ ...btnSm,fontSize:11,background:inCart?'var(--bgDanger)':'var(--bgSuccess)',color:inCart?'var(--textMuted)':'#16a34a' }}
                  onClick={() => inCart ? removeFromCart(i.id) : addToCart(i)}>
                  {inCart ? '✕ Remove' : '＋ Add'}
                </button>
              </div>
            );
          })}
        </div>
      </Card>

      <div>
        <Card title="Issuance Details">
          <Field label="Department *">
            <select style={inputStyle} value={dept} onChange={e => setDept(e.target.value)}>
              <option value="">— Select Department —</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{DEPT_LABELS[d]}</option>)}
            </select>
          </Field>
          <Field label="Requested By">
            <div style={{ position:'relative' }}>
              <input style={{ ...inputStyle,paddingRight:32 }}
                value={reqSearch}
                onChange={e => { setReqSearch(e.target.value); setReqBy(e.target.value); setShowStaffDrop(true); }}
                onFocus={() => setShowStaffDrop(true)}
                onBlur={() => setTimeout(()=>setShowStaffDrop(false),180)}
                placeholder="Type name or select employee…" />
              <span style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',color:'var(--textFaint)',fontSize:12,pointerEvents:'none' }}>▼</span>
              {showStaffDrop && filteredStaff.length > 0 && (
                <div style={{ position:'absolute',top:'100%',left:0,right:0,background:'var(--bgCard)',
                              border:'1px solid var(--border)',borderRadius:4,zIndex:999,
                              maxHeight:200,overflowY:'auto',boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
                  {filteredStaff.map(s => (
                    <div key={s.id}
                      onMouseDown={() => selectStaff(s)}
                      style={{ padding:'9px 12px',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',
                               borderBottom:'1px solid var(--border)' }}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bgInput)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <span style={{ fontWeight:600,color:'var(--textPrimary)',fontSize:13 }}>{s.name}</span>
                      <span style={{ fontSize:10,color:'#c8a96e',background:'#c8a96e22',
                                     padding:'2px 8px',borderRadius:3,textTransform:'uppercase',letterSpacing:1 }}>
                        {ROLE_LABELS[s.role]||s.role}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Field>
          <Field label="Purpose / Notes">
            <textarea style={{ ...inputStyle,height:52,resize:'none' }} value={purpose}
              onChange={e => setPurpose(e.target.value)} placeholder="e.g. Morning service, room stocking…" />
          </Field>

          {cart.length > 0 && (
            <div style={{ marginTop:10 }}>
              <div style={{ fontSize:10,color:'var(--textMuted)',letterSpacing:1,textTransform:'uppercase',marginBottom:8 }}>Items to Issue</div>
              {cart.map(c => (
                <div key={c.item_id} style={{ background:'#0e0e0e',borderRadius:3,padding:'8px 10px',marginBottom:6,display:'flex',alignItems:'center',gap:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12,fontWeight:600,color:'var(--textPrimary)' }}>{c.name}</div>
                    <div style={{ fontSize:10,color:'var(--textMuted)' }}>Max: {c.available} {c.unit}</div>
                  </div>
                  <input style={{ ...inputStyle,width:80,textAlign:'center',padding:'5px 8px',
                                  border:`1px solid ${parseFloat(c.qty_issued)>c.available?'#e74c3c':'var(--borderMid)'}` }}
                    type="number" min="0.001" step="0.001" max={c.available}
                    value={c.qty_issued} onChange={e => setQty(c.item_id, e.target.value)}
                    placeholder="qty" />
                  <span style={{ fontSize:10,color:'var(--textMuted)',minWidth:30 }}>{c.unit}</span>
                  <button style={{ background:'none',border:'none',color:'var(--textFaint)',cursor:'pointer',fontSize:14 }}
                    onClick={() => removeFromCart(c.item_id)}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display:'flex',gap:8,marginTop:14 }}>
            <button style={{ ...btnSm,flex:1 }} onClick={onBack}>← Back</button>
            <button style={{ ...btnPrimary,flex:2,background:'#2ecc71',color:'#111' }}
              disabled={loading} onClick={submit}>
              {loading ? 'Processing…' : '✓ Issue & Print Voucher'}
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function IssuanceDetail({ issuance, notify, onBack }) {
  return (
    <Card title={`Issuance ${issuance.ref}`}>
      <div style={{ display:'flex',gap:8,marginBottom:14 }}>
        <button style={btnSm} onClick={onBack}>← Back</button>
        {issuance.status === 'issued' && (
          <button style={{ ...btnSm,background:'var(--bgSuccess)',color:'#2ecc71' }}
            onClick={() => printIssuanceVoucher(issuance)}>🖨 Print Voucher</button>
        )}
      </div>
      <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:10,marginBottom:14,
                    background:'var(--bgInput)',padding:12,borderRadius:4 }}>
        <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Department</div><div style={{ fontWeight:700,color:'var(--textPrimary)' }}>{DEPT_LABELS[issuance.department]||issuance.department}</div></div>
        <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Status</div><div style={{ fontWeight:700,color:issuance.status==='issued'?'#2ecc71':'#c8a96e',textTransform:'uppercase' }}>{issuance.status}</div></div>
        <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Requested By</div><div style={{ color:'var(--textPrimary)' }}>{issuance.requested_by||'—'}</div></div>
        <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Issued By</div><div style={{ color:'var(--textPrimary)' }}>{issuance.issued_by_name}</div></div>
        {issuance.issued_at && <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Issued At</div><div style={{ color:'var(--textPrimary)' }}>{new Date(issuance.issued_at).toLocaleString('en-KE')}</div></div>}
        {issuance.purpose && <div style={{ gridColumn:'1/-1' }}><div style={{ fontSize:10,color:'var(--textMuted)' }}>Purpose</div><div style={{ color:'var(--textPrimary)' }}>{issuance.purpose}</div></div>}
      </div>
      <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
        <thead><tr style={{ background:'var(--bgInput)' }}>
          {['Item','Qty Issued','Unit','Unit Cost','Total Cost'].map(h =>
            <th key={h} style={{ padding:'7px 10px',textAlign:'left',color:'var(--textSecond)',fontSize:10,letterSpacing:1,fontWeight:700,borderBottom:'2px solid var(--border)' }}>{h}</th>)}
        </tr></thead>
        <tbody>{(issuance.issuance_items||[]).map(line => (
          <tr key={line.id} style={{ borderBottom:'1px solid var(--border)' }}>
            <td style={{ padding:'7px 10px',fontWeight:600,color:'var(--textPrimary)' }}>{line.item_name}</td>
            <td style={{ padding:'7px 10px',color:'#c8a96e',fontWeight:700 }}>{parseFloat(line.qty_issued)}</td>
            <td style={{ padding:'7px 10px',color:'var(--textSecond)' }}>{line.item_unit}</td>
            <td style={{ padding:'7px 10px',color:'var(--textSecond)' }}>{fmtKES(line.unit_cost)}</td>
            <td style={{ padding:'7px 10px',color:'#2ecc71',fontWeight:600 }}>{fmtKES(line.total_cost)}</td>
          </tr>
        ))}</tbody>
        <tfoot><tr>
          <td colSpan={4} style={{ padding:'8px 10px',fontWeight:700,color:'var(--textPrimary)',textAlign:'right' }}>TOTAL VALUE</td>
          <td style={{ padding:'8px 10px',fontWeight:700,color:'#c8a96e',fontSize:14 }}>{fmtKES(issuance.total_value)}</td>
        </tr></tfoot>
      </table>
    </Card>
  );
}


// ─── Shopping List / Purchase Requests Panel ──────────────────────────────────
function ShoppingListPanel({ shoppingLists, items: lowItems, notify, onUpdate, user }) {
  const [mode, setMode] = useState('list'); // 'list' | 'new' | 'view'
  const [current, setCurrent] = useState(null);
  const [generating, setGenerating] = useState(false);

  const canApprove = user?.role === 'admin' || user?.role === 'restaurant_manager';

  const autoGenerate = async () => {
    setGenerating(true);
    try {
      await api.generateShoppingList();
      notify('Shopping list generated from low-stock items!');
      onUpdate();
    } catch(e) { notify(e.response?.data?.error || 'No low-stock items found', 'error'); }
    finally { setGenerating(false); }
  };

  const approve = async (id) => {
    try { await api.approveShoppingList(id); notify('Shopping list approved!'); onUpdate(); }
    catch { notify('Approval failed', 'error'); }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    try { await api.rejectShoppingList(id, { reason }); notify('Shopping list rejected'); onUpdate(); }
    catch { notify('Rejection failed', 'error'); }
  };

  const markPurchased = async (id) => {
    try { await api.markShoppingListPurchased(id); notify('Marked as purchased!'); onUpdate(); }
    catch { notify('Failed', 'error'); }
  };

  if (mode === 'new') return <NewShoppingListForm items={lowItems} notify={notify} onUpdate={() => { onUpdate(); setMode('list'); }} onBack={() => setMode('list')} />;
  if (mode === 'view' && current) return <ShoppingListDetail sl={current} notify={notify} canApprove={canApprove} onApprove={approve} onReject={reject} onPurchased={markPurchased} onUpdate={onUpdate} onBack={() => setMode('list')} />;

  const STATUS_COLORS_MAP = {
    pending:'#c8a96e', manager_approved:'#e67e22', accountant_approved:'#2ecc71',
    approved:'#2ecc71', rejected:'#e74c3c', purchased:'#3498db'
  };
  const statusColor = s => STATUS_COLORS_MAP[s] || '#888';
  const statusLabel = s => ({
    pending:'Pending Manager', manager_approved:'Manager ✓ — Acct Pending',
    accountant_approved:'Fully Approved', approved:'Approved',
    rejected:'Rejected', purchased:'Purchased'
  })[s] || s;

  return (
    <div>
      <div style={{ display:'flex',gap:10,marginBottom:14,flexWrap:'wrap',alignItems:'center' }}>
        <button style={btnPrimary} onClick={() => setMode('new')}>＋ New Shopping List</button>
        <button style={{ ...btnSm,background:'#1a2a0a',color:'#c8a96e',border:'1px solid #c8a96e44',padding:'7px 16px',fontSize:12 }}
          disabled={generating} onClick={autoGenerate}>
          {generating ? '⏳ Generating…' : '🤖 Auto-Generate from Low Stock'}
        </button>
        {lowItems.length > 0 && (
          <div style={{ background:'#1a0a0a',border:'1px solid #e74c3c44',borderRadius:4,padding:'6px 12px',fontSize:12,color:'#e74c3c' }}>
            ⚠️ {lowItems.length} items below reorder level
          </div>
        )}
      </div>

      {shoppingLists.length === 0 ? <Empty text="No shopping lists yet — create one or auto-generate from low stock" /> : (
        <div style={{ display:'grid',gap:10 }}>
          {shoppingLists.map(sl => {
            const sc = statusColor(sl.status);
            return (
              <div key={sl.id} style={{ background:'var(--bgInput)',border:`1px solid ${sc}33`,
                                        borderLeft:`4px solid ${sc}`,borderRadius:4,padding:'12px 14px',
                                        display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10 }}>
                <div>
                  <div style={{ fontWeight:700,color:'var(--textPrimary)',fontSize:14 }}>{sl.ref}</div>
                  <div style={{ fontSize:12,color:'var(--textSecond)',marginTop:2 }}>{sl.title}</div>
                  <div style={{ fontSize:11,color:'var(--textMuted)',marginTop:2,display:'flex',gap:10 }}>
                    <span>📋 {sl.items?.length||0} items</span>
                    <span style={{ color:'#c8a96e' }}>Est: {fmtKES(sl.total_estimated)}</span>
                    <span>By: {sl.created_by_name}</span>
                    <span>{new Date(sl.created_at).toLocaleDateString('en-KE')}</span>
                  </div>
                </div>
                <div style={{ display:'flex',gap:8,alignItems:'center',flexWrap:'wrap' }}>
                  {/* Approval stamps */}
                  {(sl.status==='manager_approved'||sl.status==='accountant_approved'||sl.status==='purchased') && (
                    <div style={{ display:'flex',gap:6,alignItems:'center',
                                  padding:'4px 8px',background:'rgba(200,169,110,0.06)',
                                  border:'1px solid rgba(200,169,110,0.15)',borderRadius:6 }}>
                      {['manager_approved','accountant_approved','purchased'].includes(sl.status) && (
                        <ApprovalStamp role="manager" by={sl.approved_by_name} at={sl.approved_at} size={52}/>
                      )}
                      {(sl.status==='accountant_approved'||sl.status==='purchased') && (
                        <ApprovalStamp role="accountant" by={sl.accountant_approved_by_name} at={sl.accountant_approved_at} size={52}/>
                      )}
                    </div>
                  )}
                  <span style={{ padding:'3px 10px',borderRadius:3,fontSize:10,fontWeight:700,
                                 background:`${sc}22`,color:sc,textTransform:'uppercase',letterSpacing:1 }}>
                    {statusLabel(sl.status)}
                  </span>
                  <button style={{ ...btnSm,fontSize:11 }} onClick={() => { setCurrent(sl); setMode('view'); }}>👁 View</button>
                  {(sl.status==='accountant_approved'||sl.status==='approved') && (
                    <button style={{ ...btnSm,fontSize:11,background:'#0a1a2a',color:'#3498db',border:'1px solid #3498db44' }}
                      onClick={() => printShoppingList(sl)}>🖨 Print</button>
                  )}
                  {canApprove && sl.status==='pending' && (
                    <>
                      <button style={{ ...btnSm,fontSize:11,background:'#0a1a0a',color:'#2ecc71',border:'1px solid #2ecc7144' }}
                        onClick={() => approve(sl.id)}>✓ Manager Approve</button>
                      <button style={{ ...btnSm,fontSize:11,background:'#1a0a0a',color:'#e74c3c',border:'1px solid #e74c3c44' }}
                        onClick={() => reject(sl.id)}>✕ Reject</button>
                    </>
                  )}
                  {sl.status==='accountant_approved' && (
                    <button style={{ ...btnSm,fontSize:11,background:'#0a0a1a',color:'#9b59b6',border:'1px solid #9b59b644' }}
                      onClick={() => markPurchased(sl.id)}>✓ Mark Purchased</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NewShoppingListForm({ items: lowItems, notify, onUpdate, onBack }) {
  const bp = useBreakpoint();
  const [title, setTitle]   = useState('Restock Request');
  const [notes, setNotes]   = useState('');
  const [cart, setCart]     = useState(
    lowItems.map(i => ({
      stock_item: i.id, item_name: i.name, unit: i.unit,
      qty_required: Math.max(parseFloat(i.reorder_level)*2 - parseFloat(i.current_stock), parseFloat(i.reorder_level)).toFixed(3),
      estimated_cost: i.unit_cost,
      current_stock: i.current_stock, reorder_level: i.reorder_level,
      notes: `Current: ${parseFloat(i.current_stock)} ${i.unit}`,
    }))
  );
  const [customItem, setCustomItem] = useState({ item_name:'', qty_required:'1', unit:'piece', estimated_cost:'0', notes:'' });
  const [loading, setLoading] = useState(false);

  const removeItem = (idx) => setCart(c => c.filter((_,i)=>i!==idx));
  const updateItem = (idx, field, val) => setCart(c => c.map((item,i) => i===idx ? {...item,[field]:val} : item));

  const addCustom = () => {
    if (!customItem.item_name.trim()) return;
    setCart(c => [...c, { ...customItem, stock_item: null }]);
    setCustomItem({ item_name:'', qty_required:'1', unit:'piece', estimated_cost:'0', notes:'' });
  };

  const submit = async () => {
    if (cart.length === 0) { notify('Add at least one item', 'error'); return; }
    setLoading(true);
    try {
      const res = await api.createShoppingList({ title, notes, status:'pending' });
      const slId = res.data.id;
      await api.setShoppingListItems(slId, {
        items: cart.map(c => ({
          stock_item: c.stock_item || null,
          item_name: c.item_name,
          qty_required: parseFloat(c.qty_required)||1,
          unit: c.unit,
          estimated_cost: parseFloat(c.estimated_cost)||0,
          notes: c.notes,
          current_stock: parseFloat(c.current_stock)||0,
          reorder_level: parseFloat(c.reorder_level)||0,
        }))
      });
      notify('Shopping list created — awaiting manager approval');
      onUpdate();
    } catch(e) { notify(e.response?.data?.error || 'Failed to create shopping list', 'error'); }
    finally { setLoading(false); }
  };

  const totalEst = cart.reduce((s,i) => s + (parseFloat(i.qty_required)||0)*(parseFloat(i.estimated_cost)||0), 0);

  return (
    <div>
      <div style={{ display:'flex',gap:10,alignItems:'center',marginBottom:14 }}>
        <button style={btnSm} onClick={onBack}>← Back</button>
        <div style={{ fontWeight:700,color:'var(--textPrimary)',fontSize:16 }}>🛒 New Shopping List</div>
      </div>
      <div style={{ display:'grid',gridTemplateColumns: bp === 'sm' ? '1fr' : '1fr 380px',gap:16,alignItems:'start' }}>
        <div>
          <Card title="Items to Purchase">
            {cart.length === 0 ? <Empty text="No items — add from below or auto-populate from low stock" /> : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                  <thead><tr style={{ background:'var(--bgInput)' }}>
                    {['Item','Current Stock','Reorder Level','Qty to Buy','Unit','Est. Cost/Unit','Line Total','Notes',''].map(h=>
                      <th key={h} style={{ padding:'7px 8px',textAlign:'left',color:'var(--textSecond)',fontSize:10,fontWeight:700,borderBottom:'2px solid var(--border)',whiteSpace:'nowrap' }}>{h}</th>
                    )}
                  </tr></thead>
                  <tbody>
                    {cart.map((c,idx) => (
                      <tr key={idx} style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={{ padding:'6px 8px',fontWeight:600,color:'var(--textPrimary)' }}>{c.item_name}</td>
                        <td style={{ padding:'6px 8px',color:'#e74c3c',fontWeight:600,fontSize:11 }}>{parseFloat(c.current_stock||0)} {c.unit}</td>
                        <td style={{ padding:'6px 8px',color:'#c8a96e',fontSize:11 }}>{parseFloat(c.reorder_level||0)} {c.unit}</td>
                        <td style={{ padding:'6px 8px' }}>
                          <input style={{ ...inputStyle,width:80,padding:'4px 6px',textAlign:'center' }}
                            type="number" min="0.001" step="0.001"
                            value={c.qty_required} onChange={e=>updateItem(idx,'qty_required',e.target.value)} />
                        </td>
                        <td style={{ padding:'6px 8px',color:'var(--textMuted)' }}>{c.unit}</td>
                        <td style={{ padding:'6px 8px' }}>
                          <input style={{ ...inputStyle,width:90,padding:'4px 6px',textAlign:'right' }}
                            type="number" min="0" step="0.01"
                            value={c.estimated_cost} onChange={e=>updateItem(idx,'estimated_cost',e.target.value)} />
                        </td>
                        <td style={{ padding:'6px 8px',color:'#c8a96e',fontWeight:600 }}>
                          {fmtKES((parseFloat(c.qty_required)||0)*(parseFloat(c.estimated_cost)||0))}
                        </td>
                        <td style={{ padding:'6px 8px' }}>
                          <input style={{ ...inputStyle,width:120,padding:'4px 6px',fontSize:11 }}
                            value={c.notes} onChange={e=>updateItem(idx,'notes',e.target.value)} placeholder="Optional…" />
                        </td>
                        <td style={{ padding:'6px 8px' }}>
                          <button style={{ background:'none',border:'none',color:'#e74c3c',cursor:'pointer',fontSize:14 }}
                            onClick={()=>removeItem(idx)}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan={6} style={{ padding:'8px',fontWeight:700,color:'var(--textPrimary)',textAlign:'right',fontSize:13 }}>TOTAL ESTIMATED COST</td>
                    <td style={{ padding:'8px',fontWeight:700,color:'#c8a96e',fontSize:14 }}>{fmtKES(totalEst)}</td>
                    <td colSpan={2} />
                  </tr></tfoot>
                </table>
              </div>
            )}
            {/* Add custom item row */}
            <div style={{ marginTop:14,background:'var(--bgInput)',borderRadius:4,padding:12,border:'1px dashed var(--border)' }}>
              <div style={{ fontSize:11,color:'var(--textMuted)',marginBottom:8,letterSpacing:1,textTransform:'uppercase' }}>Add Custom Item</div>
              <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr auto', minWidth:400,gap:8 }}>
                <input style={{ ...inputStyle,padding:'6px 8px' }} placeholder="Item name *"
                  value={customItem.item_name} onChange={e=>setCustomItem({...customItem,item_name:e.target.value})} />
                <input style={{ ...inputStyle,padding:'6px 8px' }} type="number" placeholder="Qty" min="0.001"
                  value={customItem.qty_required} onChange={e=>setCustomItem({...customItem,qty_required:e.target.value})} />
                <input style={{ ...inputStyle,padding:'6px 8px' }} placeholder="Unit"
                  value={customItem.unit} onChange={e=>setCustomItem({...customItem,unit:e.target.value})} />
                <input style={{ ...inputStyle,padding:'6px 8px' }} type="number" placeholder="Est. cost"
                  value={customItem.estimated_cost} onChange={e=>setCustomItem({...customItem,estimated_cost:e.target.value})} />
                <button style={{ ...btnSm,background:'var(--bgSuccess)',color:'#2ecc71',padding:'6px 12px' }} onClick={addCustom}>＋ Add</button>
              </div>
            </div>
          </Card>
        </div>
        <Card title="List Details">
          <Field label="Title">
            <input style={inputStyle} value={title} onChange={e=>setTitle(e.target.value)} />
          </Field>
          <Field label="Notes">
            <textarea style={{ ...inputStyle,height:80,resize:'none' }} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Any special notes for the manager…" />
          </Field>
          <div style={{ background:'#1a1a0a',border:'1px solid #c8a96e44',borderRadius:4,padding:'10px 12px',marginBottom:14,fontSize:12 }}>
            <div style={{ color:'var(--textMuted)',fontSize:10,letterSpacing:1,textTransform:'uppercase',marginBottom:4 }}>Summary</div>
            <div style={{ display:'flex',justifyContent:'space-between' }}>
              <span style={{ color:'var(--textSecond)' }}>Items:</span>
              <span style={{ color:'#c8a96e',fontWeight:700 }}>{cart.length}</span>
            </div>
            <div style={{ display:'flex',justifyContent:'space-between',marginTop:4 }}>
              <span style={{ color:'var(--textSecond)' }}>Total Est.:</span>
              <span style={{ color:'#c8a96e',fontWeight:700 }}>{fmtKES(totalEst)}</span>
            </div>
          </div>
          <div style={{ background:'#0a1a0a',border:'1px solid #2ecc7133',borderRadius:4,padding:'8px 12px',marginBottom:14,fontSize:11,color:'#668866' }}>
            📋 This list will be sent to the manager for approval before purchasing. Print is only enabled after approval.
          </div>
          <div style={{ display:'flex',gap:8 }}>
            <button style={{ ...btnSm,flex:1 }} onClick={onBack}>← Cancel</button>
            <button style={{ ...btnPrimary,flex:2,background:'#c8a96e',color:'#111' }} disabled={loading} onClick={submit}>
              {loading ? 'Saving…' : '📤 Submit for Approval'}
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ShoppingListDetail({ sl, notify, canApprove, onApprove, onReject, onPurchased, onBack }) {
  const STATUS_MAP = {
    pending:             { color:'#c8a96e', label:'Pending Manager Approval' },
    manager_approved:    { color:'#e67e22', label:'Manager Approved — Awaiting Accountant' },
    accountant_approved: { color:'#2ecc71', label:'Fully Approved' },
    approved:            { color:'#2ecc71', label:'Approved' },
    rejected:            { color:'#e74c3c', label:'Rejected' },
    purchased:           { color:'#3498db', label:'Purchased' },
  };
  const { color: sc, label: slabel } = STATUS_MAP[sl.status] || { color:'var(--textMuted)', label: sl.status };
  const totalEst = (sl.items||[]).reduce((s,i)=>s+(parseFloat(i.qty_required)||0)*(parseFloat(i.estimated_cost)||0),0);

  return (
    <div>
      <div style={{ display:'flex',gap:10,alignItems:'center',marginBottom:14,flexWrap:'wrap' }}>
        <button style={btnSm} onClick={onBack}>← Back</button>
        <div style={{ fontWeight:700,color:'var(--textPrimary)',fontSize:16 }}>🛒 {sl.ref} — {sl.title}</div>
        <span style={{ padding:'3px 10px',borderRadius:3,fontSize:11,fontWeight:700,background:`${sc}22`,color:sc }}>{slabel}</span>
        {(sl.status==='accountant_approved'||sl.status==='approved') && (
          <button style={{ ...btnSm,background:'#0a1a2a',color:'#3498db',border:'1px solid #3498db44' }}
            onClick={()=>printShoppingList(sl)}>🖨 Print Shopping List</button>
        )}
        {canApprove && sl.status==='pending' && (
          <>
            <button style={{ ...btnSm,background:'#0a1a0a',color:'#2ecc71',border:'1px solid #2ecc7144' }}
              onClick={()=>onApprove(sl.id)}>✓ Manager Approve</button>
            <button style={{ ...btnSm,background:'#1a0a0a',color:'#e74c3c',border:'1px solid #e74c3c44' }}
              onClick={()=>onReject(sl.id)}>✕ Reject</button>
          </>
        )}
        {sl.status==='accountant_approved' && (
          <button style={{ ...btnSm,background:'#0a0a1a',color:'#9b59b6',border:'1px solid #9b59b644' }}
            onClick={()=>onPurchased(sl.id)}>✓ Mark Purchased</button>
        )}
      </div>

      {/* Two-step approval progress */}
      <div style={{ display:'flex',gap:0,marginBottom:16,borderRadius:8,overflow:'hidden',border:'1px solid var(--border)' }}>
        {[
          { step:'1', label:'Created', done: true, color:'#3498db' },
          { step:'2', label:'Manager Approved', done: ['manager_approved','accountant_approved','purchased'].includes(sl.status), color:'#e67e22' },
          { step:'3', label:'Accountant Approved', done: ['accountant_approved','purchased'].includes(sl.status), color:'#2ecc71' },
          { step:'4', label:'Purchased', done: sl.status==='purchased', color:'#9b59b6' },
        ].map((s,i) => (
          <div key={i} style={{ flex:1,padding:'8px 12px',background: s.done ? s.color+'33' : 'var(--bg3)',
                                 borderRight:'1px solid var(--border)',display:'flex',alignItems:'center',gap:6 }}>
            <span style={{ width:20,height:20,borderRadius:'50%',background:s.done?s.color:'var(--border)',
                           color:'white',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700 }}>{s.step}</span>
            <span style={{ fontSize:11,fontWeight:600,color:s.done?s.color:'var(--textMuted)' }}>{s.label}</span>
          </div>
        ))}
      </div>

      <Card title="Details">
        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:10,marginBottom:14,background:'var(--bgInput)',padding:12,borderRadius:4 }}>
          <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Created By</div><div style={{ fontWeight:600,color:'var(--textPrimary)' }}>{sl.created_by_name}</div></div>
          <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Created</div><div style={{ color:'var(--textSecond)' }}>{new Date(sl.created_at).toLocaleDateString('en-KE')}</div></div>
          {sl.approved_by_name && sl.approved_by_name!=='—' && <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Manager Approved By</div><div style={{ color:'#e67e22',fontWeight:600 }}>{sl.approved_by_name}</div></div>}
          {sl.accountant_approved_by_name && sl.accountant_approved_by_name!=='—' && <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Accountant Approved By</div><div style={{ color:'#2ecc71',fontWeight:600 }}>{sl.accountant_approved_by_name}</div></div>}
          {sl.accountant_notes && <div><div style={{ fontSize:10,color:'var(--textMuted)' }}>Accountant Notes</div><div style={{ color:'var(--textSecond)' }}>{sl.accountant_notes}</div></div>}
          {sl.notes && <div style={{ gridColumn:'1/-1' }}><div style={{ fontSize:10,color:'var(--textMuted)' }}>Notes</div><div style={{ color:'var(--textSecond)' }}>{sl.notes}</div></div>}
          {sl.rejection_reason && <div style={{ gridColumn:'1/-1' }}><div style={{ fontSize:10,color:'#e74c3c' }}>Rejection Reason</div><div style={{ color:'#e74c3c' }}>{sl.rejection_reason}</div></div>}
        </div>

        {/* Approval stamps — shown once any approval has happened */}
        {(['manager_approved','accountant_approved','purchased'].includes(sl.status)) && (
          <div style={{
            display:'flex', gap:16, alignItems:'flex-start', flexWrap:'wrap',
            padding:'14px 16px', marginBottom:16,
            background:'rgba(200,169,110,0.05)',
            border:'1px solid rgba(200,169,110,0.15)',
            borderRadius:6,
          }}>
            <div style={{ width:'100%', fontSize:10, color:'#666',
              letterSpacing:2, textTransform:'uppercase', marginBottom:4 }}>
              Official Approval Stamps
            </div>
            {['manager_approved','accountant_approved','purchased'].includes(sl.status) && (
              <ApprovalStamp
                role="manager"
                by={sl.approved_by_name && sl.approved_by_name!=='—' ? sl.approved_by_name : null}
                at={sl.approved_at}
                size={80}
              />
            )}
            {(sl.status==='accountant_approved'||sl.status==='purchased') && (
              <ApprovalStamp
                role="accountant"
                by={sl.accountant_approved_by_name && sl.accountant_approved_by_name!=='—' ? sl.accountant_approved_by_name : null}
                at={sl.accountant_approved_at}
                size={80}
              />
            )}
          </div>
        )}

        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
          <thead><tr style={{ background:'var(--bgInput)' }}>
            {['Item','Current Stock','Reorder Level','Qty to Buy','Unit','Est. Cost','Line Total','Notes'].map(h=>
              <th key={h} style={{ padding:'7px 10px',textAlign:'left',color:'var(--textSecond)',fontSize:10,fontWeight:700,borderBottom:'2px solid var(--border)' }}>{h}</th>
            )}
          </tr></thead>
          <tbody>
            {(sl.items||[]).map(item => (
              <tr key={item.id} style={{ borderBottom:'1px solid var(--border)' }}>
                <td style={{ padding:'8px 10px',fontWeight:600,color:'var(--textPrimary)' }}>{item.item_name}</td>
                <td style={{ padding:'8px 10px',color:'#e74c3c',fontWeight:600 }}>{parseFloat(item.current_stock||0)} {item.unit}</td>
                <td style={{ padding:'8px 10px',color:'#c8a96e' }}>{parseFloat(item.reorder_level||0)} {item.unit}</td>
                <td style={{ padding:'8px 10px',color:'#2ecc71',fontWeight:700 }}>{parseFloat(item.qty_required)}</td>
                <td style={{ padding:'8px 10px',color:'var(--textMuted)' }}>{item.unit}</td>
                <td style={{ padding:'8px 10px',color:'var(--textSecond)' }}>{fmtKES(item.estimated_cost)}</td>
                <td style={{ padding:'8px 10px',color:'#c8a96e',fontWeight:600 }}>{fmtKES(item.line_total)}</td>
                <td style={{ padding:'8px 10px',color:'var(--textMuted)',fontSize:11 }}>{item.notes||'—'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr>
            <td colSpan={6} style={{ padding:'8px 10px',fontWeight:700,color:'var(--textPrimary)',textAlign:'right' }}>TOTAL ESTIMATED</td>
            <td style={{ padding:'8px 10px',fontWeight:700,color:'#c8a96e',fontSize:14 }}>{fmtKES(totalEst)}</td>
            <td />
          </tr></tfoot>
        </table>
      </Card>
    </div>
  );
}

// ─── Low Stock Panel ──────────────────────────────────────────────────────────
function LowStockPanel({ items, notify, onUpdate }) {
  const reorder = async (item) => {
    notify(`Reorder flagged for ${item.name} — contact supplier: ${item.supplier||'(no supplier set)'}`, 'success');
  };

  if (items.length === 0) return (
    <Card title="Low Stock Alerts">
      <div style={{ textAlign:'center',padding:'40px 0' }}>
        <div style={{ fontSize:40,marginBottom:8 }}>✅</div>
        <div style={{ color:'var(--textMuted)',fontSize:14 }}>All items are above reorder levels!</div>
      </div>
    </Card>
  );

  return (
    <Card title={`⚠ Low Stock Alerts (${items.length} items need attention)`}>
      <div style={{ background:'#1a0a0a',border:'1px solid #e74c3c44',borderRadius:4,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#e74c3c' }}>
        These items are at or below their reorder level. Raise a purchase order or request supplies.
      </div>
      <div style={{ display:'grid',gap:10 }}>
        {items.map(item => (
          <div key={item.id} style={{ background:'var(--bgInput)',border:'1px solid #e74c3c44',borderLeft:'4px solid #e74c3c',borderRadius:4,padding:'12px 14px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10 }}>
            <div>
              <div style={{ fontWeight:700,color:'var(--textPrimary)',fontSize:14 }}>{item.name}</div>
              <div style={{ fontSize:12,color:'var(--textSecond)',marginTop:2 }}>
                {CATEGORIES.find(c=>c.v===item.category)?.label} · Supplier: {item.supplier||'—'}
              </div>
              <div style={{ display:'flex',gap:16,marginTop:6,fontSize:12 }}>
                <span style={{ color:'#e74c3c',fontWeight:700 }}>In stock: {parseFloat(item.current_stock)} {item.unit}</span>
                <span style={{ color:'var(--textMuted)' }}>Reorder at: {parseFloat(item.reorder_level)} {item.unit}</span>
                <span style={{ color:'var(--textMuted)' }}>Unit cost: {fmtKES(item.unit_cost)}</span>
              </div>
            </div>
            <button style={{ ...btnSm,background:'var(--bgDanger)',color:'#e74c3c' }} onClick={() => reorder(item)}>
              📞 Flag for Reorder
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}


// ─── Movement Log ─────────────────────────────────────────────────────────────
function MovementLog({ logs, onExport }) {
  const TYPE_COLORS = { receive:'#2ecc71',count:'#3498db',writeoff:'#e74c3c',return:'#e67e22',transfer:'#9b59b6',other:'#888' };
  return (
    <Card title={`Stock Movement Log (${logs.length} recent entries)`}
      action={onExport && (
        <div style={{ display:'flex', gap:6 }}>
          <button className="btn btn-default btn-sm" onClick={() => onExport('excel')}>⬇ Excel</button>
          <button className="btn btn-primary btn-sm" onClick={() => onExport('pdf')}>⬇ PDF</button>
        </div>
      )}>
      {logs.length === 0 ? <Empty text="No movements recorded" /> :
        <Table
          headers={['Date','Item','Type','Before','Change','After','By','Reason']}
          rows={logs.map(l => [
            <span style={{ fontSize:11,color:'var(--textMuted)' }}>{new Date(l.created_at).toLocaleDateString('en-KE')}</span>,
            <span style={{ fontWeight:600,color:'var(--textPrimary)',fontSize:12 }}>{l.item_name}</span>,
            <span style={{ padding:'2px 7px',borderRadius:3,fontSize:10,fontWeight:700,
                           background:(TYPE_COLORS[l.adj_type]||'#888')+'22',
                           color:TYPE_COLORS[l.adj_type]||'#888',textTransform:'capitalize' }}>{l.adj_type}</span>,
            <span style={{ color:'var(--textSecond)' }}>{parseFloat(l.qty_before)}</span>,
            <span style={{ fontWeight:700,color:l.qty_change>=0?'#2ecc71':'#e74c3c' }}>{l.qty_change>=0?'+':''}{parseFloat(l.qty_change)}</span>,
            <span style={{ fontWeight:700,color:'var(--textPrimary)' }}>{parseFloat(l.qty_after)}</span>,
            <span style={{ fontSize:11,color:'var(--textMuted)' }}>{l.adjusted_by_name}</span>,
            <span style={{ fontSize:11,color:'var(--textMuted)' }}>{l.reason||l.reference||'—'}</span>,
          ])}
        />
      }
    </Card>
  );
}


// ─── Purchases Panel ──────────────────────────────────────────────────────────
function PurchasesPanel({ purchases, items, shoppingLists, notify, onUpdate, user }) {
  const [view, setView]       = useState('list'); // 'list' | 'form' | 'detail'
  const [selected, setSelected] = useState(null);
  const [form, setForm]       = useState({
    shopping_list:'', supplier:'', invoice_number:'', purchase_date: new Date().toISOString().slice(0,10),
    notes:'',
  });
  const [lineItems, setLineItems] = useState([]);
  const [saving, setSaving]   = useState(false);
  const [confirming, setConfirming] = useState(false);

  const approvedLists = shoppingLists.filter(s => s.status === 'accountant_approved');
  const blankLine = { stock_item:'', qty_ordered:'', qty_received:'', unit_cost:'' };

  const addLine   = () => setLineItems(p => [...p, { ...blankLine }]);
  const removeLine = (i) => setLineItems(p => p.filter((_,j) => j !== i));
  const updateLine = (i, field, val) => setLineItems(p => p.map((l,j) => j===i ? {...l,[field]:val} : l));

  const loadFromSL = (slId) => {
    const sl = shoppingLists.find(s => String(s.id) === String(slId));
    if (!sl) { setLineItems([]); return; }
    setLineItems(sl.items.map(item => ({
      stock_item: item.stock_item || '',
      qty_ordered: item.qty_required || '',
      qty_received: item.qty_required || '',
      unit_cost: item.estimated_cost || '',
    })));
    if (sl.notes) setForm(p => ({ ...p, notes: sl.notes }));
  };

  const startNew = () => {
    setForm({ shopping_list:'', supplier:'', invoice_number:'', purchase_date: new Date().toISOString().slice(0,10), notes:'' });
    setLineItems([{ ...blankLine }]);
    setView('form');
  };

  const save = async () => {
    if (!form.supplier) { notify('Supplier name is required','error'); return; }
    if (lineItems.filter(l => l.stock_item).length === 0) { notify('Add at least one item','error'); return; }
    setSaving(true);
    try {
      const res = await api.createPurchase(form);
      const pid = res.data.id;
      await api.setPurchaseItems(pid, {
        items: lineItems.filter(l => l.stock_item).map(l => ({
          stock_item: l.stock_item,
          qty_ordered: parseFloat(l.qty_ordered) || 0,
          qty_received: parseFloat(l.qty_received) || 0,
          unit_cost: parseFloat(l.unit_cost) || 0,
        })),
      });
      notify('Purchase draft saved!');
      onUpdate();
      setView('list');
    } catch(e) { notify(e.response?.data?.detail || 'Error saving purchase','error'); }
    finally { setSaving(false); }
  };

  const confirmPurchase = async (p) => {
    if (!window.confirm(`Confirm purchase ${p.ref}? This will update stock levels for all received items.`)) return;
    setConfirming(true);
    try {
      await api.confirmPurchase(p.id);
      notify('Purchase confirmed — stock levels updated! ✅');
      onUpdate();
      setView('list');
    } catch(e) { notify(e.response?.data?.error || 'Error confirming','error'); }
    finally { setConfirming(false); }
  };

  const cancelPurchase = async (p) => {
    if (!window.confirm(`Cancel purchase ${p.ref}?`)) return;
    try { await api.cancelPurchase(p.id); notify('Purchase cancelled'); onUpdate(); }
    catch(e) { notify('Error cancelling','error'); }
  };

  const total = lineItems.reduce((s,l) => s + (parseFloat(l.qty_received)||0)*(parseFloat(l.unit_cost)||0), 0);

  const STATUS_COLORS = { draft:'#e67e22', confirmed:'#2ecc71', cancelled:'#888' };

  if (view === 'form') return (
    <Card title="New Purchase Order" action={
      <button style={{ ...btnSm, background:'#2a2a1a', color:'#e67e22' }} onClick={() => setView('list')}>← Back</button>
    }>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12, marginBottom:16 }}>
        <Field label="Link to Shopping List (optional)">
          <select style={inputStyle} value={form.shopping_list} onChange={e => {
            setForm(p => ({...p, shopping_list: e.target.value}));
            if (e.target.value) loadFromSL(e.target.value);
          }}>
            <option value="">— None —</option>
            {approvedLists.map(sl => <option key={sl.id} value={sl.id}>{sl.ref} — {sl.title}</option>)}
          </select>
        </Field>
        <Field label="Supplier *">
          <input style={inputStyle} placeholder="Supplier name" value={form.supplier}
            onChange={e => setForm(p => ({...p, supplier: e.target.value}))} />
        </Field>
        <Field label="Invoice Number">
          <input style={inputStyle} placeholder="INV-XXXX" value={form.invoice_number}
            onChange={e => setForm(p => ({...p, invoice_number: e.target.value}))} />
        </Field>
        <Field label="Purchase Date">
          <input type="date" style={inputStyle} value={form.purchase_date}
            onChange={e => setForm(p => ({...p, purchase_date: e.target.value}))} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea style={{ ...inputStyle, minHeight:50 }} value={form.notes}
          onChange={e => setForm(p => ({...p, notes: e.target.value}))} />
      </Field>

      <div style={{ margin:'20px 0 8px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <h4 style={{ color:'var(--textPrimary)', margin:0 }}>Line Items</h4>
        <button style={{ ...btnSm, background:'#1a2a3a', color:'#3498db' }} onClick={addLine}>+ Add Item</button>
      </div>
      {lineItems.map((l, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr auto', minWidth:400, gap:8, marginBottom:8, alignItems:'end' }}>
          <Field label={i===0?'Stock Item':''}>
            <select style={inputStyle} value={l.stock_item} onChange={e => updateLine(i,'stock_item',e.target.value)}>
              <option value="">— Select —</option>
              {items.map(it => <option key={it.id} value={it.id}>{it.name} ({it.unit})</option>)}
            </select>
          </Field>
          <Field label={i===0?'Qty Ordered':''}>
            <input type="number" style={inputStyle} placeholder="0" value={l.qty_ordered}
              onChange={e => updateLine(i,'qty_ordered',e.target.value)} />
          </Field>
          <Field label={i===0?'Qty Received':''}>
            <input type="number" style={inputStyle} placeholder="0" value={l.qty_received}
              onChange={e => updateLine(i,'qty_received',e.target.value)} />
          </Field>
          <Field label={i===0?'Unit Cost (KES)':''}>
            <input type="number" style={inputStyle} placeholder="0.00" value={l.unit_cost}
              onChange={e => updateLine(i,'unit_cost',e.target.value)} />
          </Field>
          <button className="btn btn-danger btn-sm" onClick={() => removeLine(i)}>✕</button>
        </div>
      ))}

      <div style={{ marginTop:16, padding:'12px 16px', background:'var(--bg3)', borderRadius:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ color:'var(--textSecond)', fontSize:13 }}>Estimated Total</span>
        <span style={{ color:'#c8a96e', fontWeight:700, fontSize:18 }}>{fmtKES(total)}</span>
      </div>
      <div style={{ marginTop:16, display:'flex', gap:8 }}>
        <button style={{ ...btnPrimary }} onClick={save} disabled={saving}>{saving?'Saving…':'💾 Save Draft'}</button>
        <button style={{ ...btnSm, background:'#2a2a1a', color:'var(--textMuted)' }} onClick={() => setView('list')}>Cancel</button>
      </div>
    </Card>
  );

  if (view === 'detail' && selected) return (
    <Card title={`Purchase ${selected.ref}`} action={
      <button style={{ ...btnSm, background:'#2a2a1a', color:'#e67e22' }} onClick={() => { setSelected(null); setView('list'); }}>← Back</button>
    }>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:12, marginBottom:16 }}>
        <div style={{ background:'var(--bg3)', padding:12, borderRadius:8 }}>
          <div style={{ fontSize:11, color:'var(--textMuted)' }}>Status</div>
          <div style={{ fontWeight:700, color: STATUS_COLORS[selected.status]||'#888', marginTop:4, textTransform:'capitalize' }}>{selected.status}</div>
        </div>
        <div style={{ background:'var(--bg3)', padding:12, borderRadius:8 }}>
          <div style={{ fontSize:11, color:'var(--textMuted)' }}>Supplier</div>
          <div style={{ fontWeight:600, color:'var(--textPrimary)', marginTop:4 }}>{selected.supplier||'—'}</div>
        </div>
        <div style={{ background:'var(--bg3)', padding:12, borderRadius:8 }}>
          <div style={{ fontSize:11, color:'var(--textMuted)' }}>Date</div>
          <div style={{ fontWeight:600, color:'var(--textPrimary)', marginTop:4 }}>{selected.purchase_date}</div>
        </div>
        <div style={{ background:'var(--bg3)', padding:12, borderRadius:8 }}>
          <div style={{ fontSize:11, color:'var(--textMuted)' }}>Invoice #</div>
          <div style={{ fontWeight:600, color:'var(--textPrimary)', marginTop:4 }}>{selected.invoice_number||'—'}</div>
        </div>
        <div style={{ background:'var(--bg3)', padding:12, borderRadius:8 }}>
          <div style={{ fontSize:11, color:'var(--textMuted)' }}>Shopping List</div>
          <div style={{ fontWeight:600, color:'var(--textPrimary)', marginTop:4 }}>{selected.shopping_list_ref||'—'}</div>
        </div>
        <div style={{ background:'var(--bg3)', padding:12, borderRadius:8 }}>
          <div style={{ fontSize:11, color:'var(--textMuted)' }}>Total Value</div>
          <div style={{ fontWeight:700, color:'#c8a96e', marginTop:4 }}>{fmtKES(selected.computed_total||selected.total_amount)}</div>
        </div>
      </div>
      {selected.notes && <p style={{ color:'var(--textSecond)', marginBottom:16, fontSize:13 }}>📝 {selected.notes}</p>}

      <h4 style={{ color:'var(--textPrimary)', marginBottom:8 }}>Items Received</h4>
      <Table
        headers={['Item','Qty Ordered','Qty Received','Unit Cost','Line Total']}
        rows={(selected.purchase_items||[]).map(pi => [
          <span style={{ fontWeight:600, color:'var(--textPrimary)' }}>{pi.item_name}</span>,
          <span style={{ color:'var(--textSecond)' }}>{parseFloat(pi.qty_ordered)} {pi.item_unit}</span>,
          <span style={{ fontWeight:700, color:'#2ecc71' }}>{parseFloat(pi.qty_received)} {pi.item_unit}</span>,
          <span style={{ color:'var(--textSecond)' }}>{fmtKES(pi.unit_cost)}</span>,
          <span style={{ fontWeight:700, color:'#c8a96e' }}>{fmtKES(pi.line_total)}</span>,
        ])}
      />
      {selected.status === 'draft' && (
        <div style={{ marginTop:16, display:'flex', gap:8 }}>
          <button style={{ ...btnPrimary, background:'linear-gradient(135deg,#27ae60,#1e8449)' }}
            onClick={() => confirmPurchase(selected)} disabled={confirming}>
            {confirming ? 'Confirming…' : '✅ Confirm Purchase & Update Stock'}
          </button>
          <button style={{ ...btnSm, background:'var(--bgDanger)', color:'#e74c3c' }}
            onClick={() => cancelPurchase(selected)}>🗑 Cancel</button>
        </div>
      )}
      {selected.status === 'confirmed' && (
        <div style={{ marginTop:12, padding:'10px 14px', background:'var(--bgSuccess)', borderRadius:8, color:'#2ecc71', fontSize:13 }}>
          ✅ Purchase confirmed — stock levels have been updated.
        </div>
      )}
    </Card>
  );

  return (
    <Card title={`Purchases (${purchases.length})`} action={
      <button style={btnPrimary} onClick={startNew}>+ New Purchase</button>
    }>
      {purchases.length === 0 ? <Empty text="No purchases recorded yet" /> :
        <Table
          headers={['Ref','Date','Supplier','Invoice','Items','Total','Status','Actions']}
          rows={purchases.map(p => [
            <span style={{ fontWeight:700, color:'#c8a96e', fontFamily:'monospace' }}>{p.ref}</span>,
            <span style={{ fontSize:12, color:'var(--textMuted)' }}>{p.purchase_date}</span>,
            <span style={{ fontWeight:600, color:'var(--textPrimary)' }}>{p.supplier||'—'}</span>,
            <span style={{ color:'var(--textSecond)', fontSize:12 }}>{p.invoice_number||'—'}</span>,
            <span style={{ color:'var(--textSecond)' }}>{(p.purchase_items||[]).length}</span>,
            <span style={{ fontWeight:700, color:'#c8a96e' }}>{fmtKES(p.computed_total||p.total_amount)}</span>,
            <span style={{ padding:'3px 8px', borderRadius:4, fontSize:11, fontWeight:700,
                           background:(STATUS_COLORS[p.status]||'#888')+'22',
                           color:STATUS_COLORS[p.status]||'#888', textTransform:'capitalize' }}>{p.status}</span>,
            <div style={{ display:'flex', gap:6 }}>
              <button style={{ ...btnSm, background:'#1a2a3a', color:'#3498db' }}
                onClick={() => { setSelected(p); setView('detail'); }}>View</button>
              {p.status === 'draft' && (
                <button style={{ ...btnSm, background:'var(--bgSuccess)', color:'#2ecc71' }}
                  onClick={() => confirmPurchase(p)} disabled={confirming}>Confirm</button>
              )}
            </div>,
          ])}
        />
      }
    </Card>
  );
}